import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AwCtx } from './ctx';
import { setItemWithRoom } from '../../services/draftMirror';
import { th } from './ui';

// ── Column sorting that stays (batch 12 tail T3, FS-11; decision Q5; TL-15) ─────────────────────────────────────────
// The Sales, Purchases and Expenses lists and the general ledger's level-1 list sort by a click on a column's header.
//
// ⛔ THE SORT IS THE SERVER'S. Each list's keys are the SAME words as its route's whitelist (routes/acctWorkspace.ts
// DOC_SORTS / PO_SORTS / EXPENSE_SORTS / NOTE_SORTS / PAYMENT_SORTS, services/expenseRecords.ts SORTS, services/ledgerAccounts.ts
// LEDGER_SORTS): the order is struck in SQL before the pager, so page 2 continues page 1 — sorting the hundred rows in
// hand would sort a page, and on REAL SMART's 828 tax invoices only 16 of the 100 largest are on today's page 1.
//
// ⛔ IT STAYS, PER VIEWER AND PER LIST, IN THIS BROWSER. One key per login, `mf_aw_sort:<userId>`, holds a JSON map
// { listId: 'key:dir' }. Every entry is checked against its list's keys when read, so a stale or forged entry is never
// sent; an entry equal to the list's default is deleted and an id that is not a list here is pruned, so the map stays a
// few hundred bytes. It is written through draftMirror's setItemWithRoom (the house rule for small settings: a full
// quota makes room rather than eating the preference); if even that fails the sort holds for this visit, with no alert
// and no throw. It survives closing a window, switching submenu, changing the period and reloading; a new device
// starts at the defaults. NOT persisted: the page (back to 1 whenever the sort changes or the list remounts), the search
// and the filters (FS-9 keeps them while the pane lives). The preference is per viewer, not per client company.
//
// ⛔ NEVER IN THE URL. A search-param write is an in-app jump the unsaved guard and setNav re-key around; the sort lives
// in component state and the viewer's own storage only. A read-only seat gets the headers too: sorting is a GET.

export type SortDir = 'asc' | 'desc';
export type ListId =
  | 'sales.quotation' | 'sales.invoice' | 'sales.tax_invoice' | 'sales.receipt' | 'sales.credit_note' | 'sales.debit_note'
  | 'purchases.orders' | 'purchases.supplier_bills' | 'purchases.notes'
  | 'expenses.records' | 'expenses.payments' | 'gl.accounts';
type ListDef = { keys: readonly string[]; def: { sort: string; dir: SortDir }; firstDesc: readonly string[];
  /** Sorts the list keeps with no header to click, each with its own control (SortChip). */
  noHeader?: readonly string[] };

// The first click on a column: dates and the list's OWN document number open newest first (the expense register's
// rule, which these lists now share); every other column — names, the supplier's number, amounts, statuses — opens
// A→Z / smallest first. A second click on the same column reverses it. The general ledger's default is its chart's
// own order (sort ''), which "Chart order" restores; its columns all open ascending.
const SALES_DATE = { sort: 'date', dir: 'desc' } as const;
// (Owner 2026-11-02, "Standardized Document Table Layout") Seven columns on every document list, so the keys below are the headers
// that remain and nothing else: Type, Paid, Outstanding, Linked, Supplier No., File, Uploaded, Expected, Lines, Bill No., VAT and By
// are off these lists. ⛔ A VIEWER'S STORED SORT ON ONE OF THEM IS NOT AN ERROR: parseEntry finds no such key, so the list opens on its
// default and the next write prunes the entry. The routes' whitelists keep those words, though nothing here sends them now, and the two
// defaults that name no header stay what they were: Supplier bills by upload, whose "Newest upload first" link puts it back (panes/Purchases.tsx),
// the general ledger by its chart. One exception: Outstanding, off the headers by the same ruling, keeps a small control of its own on the
// lists that owe (`noHeader`, SortChip): the ruling moved a figure, and "who owes the most" stayed a question. It opens largest first.
export const LIST: Readonly<Record<ListId, ListDef>> = {
  'sales.quotation': { keys: ['doc_no', 'date', 'customer', 'total', 'status'], def: SALES_DATE, firstDesc: ['doc_no', 'date'] },
  'sales.invoice': { keys: ['doc_no', 'date', 'customer', 'total', 'status'], def: SALES_DATE, firstDesc: ['doc_no', 'date', 'outstanding'], noHeader: ['outstanding'] },
  'sales.tax_invoice': { keys: ['doc_no', 'date', 'customer', 'total', 'status'], def: SALES_DATE, firstDesc: ['doc_no', 'date', 'outstanding'], noHeader: ['outstanding'] },
  // A receipt's status is the one word (panes/Documents.tsx): nothing to order by.
  'sales.receipt': { keys: ['doc_no', 'date', 'customer', 'total'], def: SALES_DATE, firstDesc: ['doc_no', 'date'] },
  // (Owner 2026-11-02) One page per type replaced `sales.note`. A viewer's stored `sales.note` is no list here any more: never read, and
  // pruned by writeEntry's next write.
  'sales.credit_note': { keys: ['doc_no', 'date', 'customer', 'total', 'status'], def: SALES_DATE, firstDesc: ['doc_no', 'date'] },
  'sales.debit_note': { keys: ['doc_no', 'date', 'customer', 'total', 'status'], def: SALES_DATE, firstDesc: ['doc_no', 'date', 'outstanding'], noHeader: ['outstanding'] },
  'purchases.orders': { keys: ['po_no', 'po_date', 'vendor', 'total', 'status'], def: { sort: 'po_date', dir: 'desc' }, firstDesc: ['po_no', 'po_date'] },
  'purchases.supplier_bills': { keys: ['expense_no', 'doc_date', 'vendor', 'total', 'status'], def: { sort: 'uploaded', dir: 'desc' }, firstDesc: ['expense_no', 'doc_date'] },
  'purchases.notes': { keys: ['note_no', 'date', 'vendor', 'amount'], def: { sort: 'date', dir: 'desc' }, firstDesc: ['date', 'note_no'] },
  'expenses.records': { keys: ['expense_no', 'date', 'supplier', 'total'], def: { sort: 'date', dir: 'desc' }, firstDesc: ['date', 'expense_no', 'outstanding'], noHeader: ['outstanding'] },
  'expenses.payments': { keys: ['pay_no', 'date', 'payee', 'total', 'status'], def: { sort: 'date', dir: 'desc' }, firstDesc: ['pay_no', 'date'] },
  'gl.accounts': { keys: ['code', 'account', 'opening', 'debit', 'credit', 'closing'], def: { sort: '', dir: 'asc' }, firstDesc: [] },
};

export const SORT_STORE_KEY = (userId: string) => `mf_aw_sort:${userId}`;

type Entry = { sort: string; dir: SortDir };
const isList = (id: string): id is ListId => Object.prototype.hasOwnProperty.call(LIST, id);
const sortsBy = (id: ListId, key: string): boolean => LIST[id].keys.includes(key) || !!LIST[id].noHeader?.includes(key);
/** One stored entry, checked against its list's own keys — null when it is not one this list would send. */
const parseEntry = (id: ListId, v: unknown): Entry | null => {
  if (typeof v !== 'string') return null;
  const m = /^([a-z_]+):(asc|desc)$/.exec(v);
  return m && sortsBy(id, m[1]) ? { sort: m[1], dir: m[2] as SortDir } : null;
};
const isDef = (id: ListId, e: Entry) => e.sort === LIST[id].def.sort && (e.sort === '' || e.dir === LIST[id].def.dir);
/** The viewer's map as stored — {} for none, for corrupt JSON, for anything not a plain object, or when storage throws. */
const readMap = (userId: string): Record<string, unknown> => {
  try {
    const raw = localStorage.getItem(SORT_STORE_KEY(userId));
    if (!raw) return {};
    const o = JSON.parse(raw);
    return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
  } catch { return {}; }
};
const readEntry = (userId: string, id: ListId): Entry => {
  const e = userId ? parseEntry(id, readMap(userId)[id]) : null;
  return e && !isDef(id, e) ? e : { ...LIST[id].def };
};
/** Rewrite the viewer's map with this list's entry: valid entries of known lists only, the default deleted. */
const writeEntry = (userId: string, id: ListId, e: Entry): void => {
  if (!userId) return;   // no login to file it under: this visit only
  const was = readMap(userId);
  const out: Record<string, string> = {};
  for (const k of Object.keys(was)) {
    if (k === id || !isList(k)) continue;
    const v = parseEntry(k, was[k]);
    if (v && !isDef(k, v)) out[k] = `${v.sort}:${v.dir}`;
  }
  if (!isDef(id, e)) out[id] = `${e.sort}:${e.dir}`;
  try {
    if (Object.keys(out).length) setItemWithRoom(SORT_STORE_KEY(userId), JSON.stringify(out));
    else localStorage.removeItem(SORT_STORE_KEY(userId));
  } catch { /* storage unavailable: the sort holds in memory for this visit */ }
};

export type ListSort = {
  sort: string; dir: SortDir;
  sortBy: (key: string) => void;
  ariaSort: (key: string) => 'ascending' | 'descending' | 'none';
  /** The list's own default order — what the server gives with no sort at all (so a caller may send none). */
  isDefault: boolean;
  reset: () => void;
};

/**
 * One list's sort, read from the viewer's stored preference on mount (so the FIRST request already carries it) and
 * written back on every change. `onChange` runs after a change made here — a list puts its page back to 1 there.
 * ⛔ Call it above any early return (rules of hooks): Documents.tsx's `notApplicable` return sits below it.
 */
export function useListSort(ctx: AwCtx, id: ListId, opts: { onChange?: () => void } = {}): ListSort {
  const uid = String(ctx?.me?.userId || '');
  const [st, setSt] = useState(() => ({ uid, id, ...readEntry(uid, id) }));
  let cur = st;
  // A pane reused for another list (Documents' six families share one component) reads THAT list's preference.
  if (st.uid !== uid || st.id !== id) { cur = { uid, id, ...readEntry(uid, id) }; setSt(cur); }
  const onChange = useRef(opts.onChange);
  onChange.current = opts.onChange;
  const apply = (e: Entry) => {
    setSt({ uid, id, ...e });
    writeEntry(uid, id, e);
    onChange.current?.();
  };
  const def = LIST[id].def;
  return {
    sort: cur.sort, dir: cur.dir,
    sortBy: (key: string) => {
      if (!sortsBy(id, key)) return;
      if (key === cur.sort) apply({ sort: key, dir: cur.dir === 'asc' ? 'desc' : 'asc' });
      else apply({ sort: key, dir: LIST[id].firstDesc.includes(key) ? 'desc' : 'asc' });
    },
    ariaSort: (key: string) => (key && key === cur.sort ? (cur.dir === 'asc' ? 'ascending' : 'descending') : 'none'),
    isDefault: isDef(id, cur),
    reset: () => { if (!isDef(id, cur)) apply({ ...def }); },
  };
}

/**
 * A sortable header: a real <button> inside the <th>, so it is reached and pressed by keyboard, with aria-sort on the
 * cell, the arrow and the accent colour on the active column only, and the one title. ⛔ MODULE LEVEL, never declared
 * inside a list's render: a component defined in a render is a new type every render, so React remounts the header on
 * the click and the keyboard focus the click left on it is lost (the expense register's old `Th`).
 */
export function SortTh({ label, k, s, align = 'left' }: { label: string; k: string; s: ListSort; align?: 'left' | 'right' }) {
  const { t } = useTranslation();
  const on = s.sort === k;
  const aria = s.ariaSort(k);
  return (
    <th style={{ ...th, textAlign: align }} aria-sort={aria}>
      <button type="button" onClick={() => s.sortBy(k)} title={t('aw_sort_title')} data-sort={k}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: on ? 'var(--accent-text)' : 'inherit', textTransform: 'inherit', letterSpacing: 'inherit', textAlign: 'inherit' }}>
        {label}{on ? (s.dir === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    </th>
  );
}

/** The same sort, for a figure with no column of its own (LIST's `noHeader`): a small button in the list's toolbar, with the same arrow. */
export function SortChip({ label, k, s }: { label: string; k: string; s: ListSort }) {
  const { t } = useTranslation();
  const on = s.sort === k;
  return (
    <button type="button" onClick={() => s.sortBy(k)} title={t('aw_sort_title')} aria-pressed={on} data-sort-chip={k}
      style={{ background: on ? 'var(--bg-app)' : 'none', border: '1px solid var(--border-strong)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', font: 'inherit', fontSize: 12, whiteSpace: 'nowrap', flex: 'none', color: on ? 'var(--accent-text)' : 'var(--text-secondary)' }}>
      <i className="ti ti-arrows-sort" aria-hidden /> {label}{on ? (s.dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </button>
  );
}

/** The query a list sends for its sort: nothing at its default (the server's own order, byte for byte). */
export const sortArgs = (s: ListSort): { sort?: string; dir?: SortDir } => (s.isDefault ? {} : { sort: s.sort, dir: s.dir });
